import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IoChevronBackSharp, IoChevronForwardSharp } from 'react-icons/io5';
import Navbar from '../../navbar';
import Header from '../../header';
import { fetchApplicationWithId, updateApplication } from '../../../store/reducers/applicationSlice';
import FormLoader from '../../forms/formLoader';
import classes from './style.module.css';
import { getstate } from '../../../store/reducers/stateSlice';
import { fetchCategories } from '../../../store/reducers/categoriesSlice';
import { createNotification } from '../../../store/reducers/notificationSlice';
import {
    setFormFieldData,
    hideAllPagesExceptCurrentFormPage,
    goToFormNextPage,
    goToFormPreviousPage,
    setPage,
} from '../../../utility/utility';
import Index from './descripancie';
import { getSignature } from '../../../store/reducers/fileSlice';
import { hasRole, ROLES } from '../../../utility/rbac';

const Descrepancies = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const user = useSelector((state) => state.auth.user);
    const authToken = user.access_token;
    const { currentApp } = useSelector((state) => state.applications);
    const applicationdata = useSelector((state) => state.applications.currentApp);
    const status = useSelector((state) => state.status.status);
    const [formData, setFormData] = useState([]);
    const [pageIds, setPageIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoaded, setIsLoaded] = useState(false);
    const [formIds, setFormIds] = useState([]);
    const [selectedFieldData, setSelectedFieldData] = useState({});
    const [signatureImage, setSignatureImage] = useState('');
    const [inputPage, setInputPage] = useState(currentPage);

    // keep page input synced
    useEffect(() => {
        setInputPage(currentPage);
    }, [currentPage]);

    // fetch app + states + categories
    const statusvalue = useCallback(() => {
        dispatch(fetchApplicationWithId({ accessToken: authToken, id: params.id }));
        dispatch(getstate(authToken));
        dispatch(fetchCategories({ accessToken: authToken }));
    }, [dispatch, authToken, params.id]);

    useEffect(() => {
        statusvalue();
    }, [statusvalue]);

    // update application state
    const selecteddata = async (e) => {
        const filterstatus = status.filter((item) => item.name === e);
        const currentTime = new Date().toLocaleTimeString();

        await dispatch(
            updateApplication({
                accessToken: authToken,
                id: params.id,
                state: filterstatus[0]?.id || '',
                currentTime,
                json_data: applicationdata?.json_data,
            })
        );

        let message = `Your Application State Changed`;
        if (e === 'in_process' || e === 1) message = `Your Application In Process`;
        else if (e === 'approved' || e === 4) message = `Your Application Approved`;
        else if (e === 'rejected' || e === 5) message = `Your Application Rejected`;
        else if (e === 'descrepency_GB_GB' || e === 2) message = `Your Application Discrepancies`;
        else if (e === 'pending_for_approval' || e === 3) message = `Your Application Pending For Approval`;
        else if (e === 'renewal' || e === 7) message = `Your Application Renewal`;
        else message = `Your Application ${e}`;

        dispatch(
            createNotification({
                message: message,
                userId: applicationdata?.user_id,
                accessToken: authToken,
                is_admin: false,
                subject: message,
                htmlMailContent: message,
                sendToGamingBoardViewers: true,
                sendToGamingBoardReviewers: true,
                sendToCompanyAdmins: true,
            })
        );

        statusvalue();
    };

    // async setup when form is loaded
    useEffect(() => {
        let isMounted = true;
        const runAsync = async () => {
            if (isLoaded && applicationdata?.json_data) {
                await setFormFieldData(applicationdata.json_data);
                const userId = applicationdata.user_id;
                try {
                    const signature = await getSignature({ userId, applicationId: params.id, authToken });
                    if (isMounted) setSignatureImage(signature);
                } catch (err) {
                    console.error('Error fetching signature:', err);
                }
            }
        };
        runAsync();
        return () => {
            isMounted = false;
        };
    }, [isLoaded, applicationdata, params.id, authToken]);

    // hide/show correct form page
    useEffect(() => {
        if (pageIds.length > 0) {
            hideAllPagesExceptCurrentFormPage(pageIds, currentPage);
        }
    }, [pageIds, currentPage]);

    // discrepancies prefill
    useEffect(() => {
        if (applicationdata?.discrepancies) {
            setSelectedFieldData(applicationdata.discrepancies);
        }
    }, [applicationdata]);

    // Apply red background to fields that are in discrepancies (on page load and when selectedFieldData changes)
    useEffect(() => {
        if (formIds.length > 0 && isLoaded) {
            // Apply red background to all fields that are in selectedFieldData
            Object.keys(selectedFieldData).forEach((discrepancyKey) => {
                // Remove 'dis_' prefix to get the actual field ID
                const fieldId = discrepancyKey.replace('dis_', '');
                const fieldElement = document.getElementById(fieldId);
                if (fieldElement) {
                    fieldElement.style.backgroundColor = '#ff0000';
                }
            });

            // Remove red background from fields that are not in selectedFieldData
            formIds.forEach((fieldId) => {
                const discrepancyKey = 'dis_' + fieldId;
                if (!selectedFieldData[discrepancyKey]) {
                    const fieldElement = document.getElementById(fieldId);
                    if (fieldElement) {
                        fieldElement.style.backgroundColor = '';
                    }
                }
            });
        }
    }, [selectedFieldData, formIds, isLoaded]);

    // click handler for discrepancies
    const handleInputChange = useCallback((event) => {
        if (hasRole(user, ROLES.VIEWER)) return; // Viewer cannot change discrepancies
        const fieldId = 'dis_' + event.target.id;
        setSelectedFieldData((prevFields) => {
            const newFields = { ...prevFields };
            if (newFields[fieldId]) {
                delete newFields[fieldId];
                event.target.style.backgroundColor = '';
            } else {
                newFields[fieldId] = { value: '', check: true };
                event.target.style.backgroundColor = '#ff0000';
            }
            return newFields;
        });
    }, []);

    // attach/remove listeners
    useEffect(() => {
        formIds.forEach((id) => {
            const inputField = document.getElementById(id);
            if (inputField) {
                inputField.removeEventListener('click', handleInputChange);
                inputField.addEventListener('click', handleInputChange);
            }
        });
        return () => {
            formIds.forEach((id) => {
                const inputField = document.getElementById(id);
                if (inputField) {
                    inputField.removeEventListener('click', handleInputChange);
                }
            });
        };
    }, [formIds, handleInputChange]);

    // inject signature
    useEffect(() => {
        if (formIds.length > 0 && signatureImage && isLoaded) {
            formIds.forEach((id) => {
                const inputField = document.getElementById(id);
                if (inputField?.type === 'image') {
                    inputField.src = signatureImage;
                }
            });
        }
    }, [signatureImage, isLoaded, formIds]);

    return (
        <div>
            <Navbar />
            <Header />
            <div className="wrapper">
                <div className="boxAppBoard boxWhite">
                    <div className="stickyFormHeader">
                        <div className="AppFormBar noShadow">
                            <div className="title">
                                {currentApp?.name} {formData.find((el) => el.id === currentPage)?.pageName}
                            </div>
                            <div className="pagination">
                                <div className="pageNum d-flex align-items-center">
                                    <IoChevronBackSharp onClick={() => goToFormPreviousPage(currentPage, setCurrentPage)} />

                                    <input
                                        type="text"
                                        value={inputPage}
                                        style={{
                                            width: 50,
                                            height: 50,
                                            textAlign: 'center',
                                            color: '#fff',
                                            background: '#323639',
                                            border: '1px solid #ccc',
                                            borderRadius: '5px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                        }}
                                        onBeforeInput={(e) => {
                                            if (!/^\d$/.test(e.data)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setInputPage(value);
                                            if (value !== '') {
                                                const pageNum = parseInt(value);
                                                if (!isNaN(pageNum)) {
                                                    setPage(pageNum, currentPage, pageIds, setCurrentPage);
                                                }
                                            }
                                        }}
                                    />

                                    <span className="slash">
                                        /<span style={{ marginLeft: 10 }}>{pageIds.length}</span>
                                    </span>
                                    <IoChevronForwardSharp onClick={() => goToFormNextPage(currentPage, pageIds, setCurrentPage)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="boardHeader">
                        <div className="boardHeadLeft">
                            <h2>Descrepancies</h2>
                        </div>
                        <div className="boardHeadRight">
                            <div>
                                <select
                                    className="form-select"
                                    value={applicationdata?.state || 'All'}
                                    disabled={hasRole(user, ROLES.VIEWER)}
                                    onChange={(e) => selecteddata(e.target.value)}
                                >
                                    <option disabled value="All">
                                        Select State
                                    </option>
                                    {status.length > 0 &&
                                        status
                                            .filter((item) =>
                                                ['in_process', 'pending_for_approval', 'descrepency_GB_GB', 'approved', 'renewal'].includes(item.name)
                                            )
                                            .map((item) => (
                                                <option key={item.name} value={item.name}>
                                                    {item.name === 'in_process' ? 'In Process'
                                                        : item.name === 'pending_for_approval' ? 'Pending f DW'
                                                            : item.name === 'descrepency_GB_GB' ? 'Discrepancies'
                                                                : item.name === 'approved' ? 'Approved'
                                                                    : item.name === 'renewal' ? 'Renewal' : ''}
                                                </option>
                                            ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className={classes['board-content']}>
                        <div className="disarea">
                            <Index
                                selectedFieldData={selectedFieldData}
                                setSelectedFieldData={setSelectedFieldData}
                                formId={params.id}
                                formIds={formIds}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                applicationUserId={applicationdata?.user_id}
                            />
                            <FormLoader
                                applicationName={applicationdata?.name}
                                setFormIds={setFormIds}
                                setPageIds={setPageIds}
                                setIsLoaded={setIsLoaded}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Descrepancies;
